<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { MAP_STYLES, ICON_ACTIVE } from '@/configs/map';
import { useGlobalStore } from '@/stores/global';
const globalStore = useGlobalStore();
const googleMapsApiKey = computed(() => globalStore.googleMapsApiKey);

const props = defineProps({
  marker: {
    type: Object,
    default: () => ({}),
  },
});

const mapEl = ref(null);
let map = null;
let markerInstance = null;

let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (gmapsPromise) return gmapsPromise;

  gmapsPromise = new Promise((resolve, reject) => {
    const id = 'gmaps-script';
    if (document.getElementById(id)) {
      const wait = () =>
        window.google?.maps ? resolve() : setTimeout(wait, 50);
      return wait();
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gmapsPromise;
}

function initMap() {
  if (!mapEl.value || !window.google?.maps) return;

  // Беремо координати безпосередньо з props.marker
  const lat = Number(props.marker?.lat);
  const lng = Number(props.marker?.lng);
  const position =
    !Number.isNaN(lat) && !Number.isNaN(lng)
      ? { lat, lng }
      : { lat: 50.4501, lng: 30.5234 };

  map = new window.google.maps.Map(mapEl.value, {
    center: position,
    zoom: 15,
    styles: MAP_STYLES,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
  });
}

function renderMarker() {
  if (!map || !props.marker) return;

  if (markerInstance) markerInstance.setMap(null);

  const lat = Number(props.marker.lat);
  const lng = Number(props.marker.lng);

  if (Number.isNaN(lat) || Number.isNaN(lng)) return;

  markerInstance = new window.google.maps.Marker({
    position: { lat, lng },
    map,
    icon: ICON_ACTIVE,
    title: props.marker.name || '',
    clickable: false,
  });

  map.setCenter({ lat, lng });
}

async function tryInitMap(apiKey) {
  try {
    await loadGoogleMaps(apiKey);
    initMap();
    renderMarker();
  } catch (error) {
    console.error('Failed to load Google Maps with dynamic API key:', error);
  }
}

// watch(...,{immediate:true}) fires synchronously during setup(), before this
// component has had its first mount — mapEl isn't bound yet at that point, and
// nextTick()/rAF don't reliably fix that since they aren't tied to this
// component's own first paint. onMounted() is the point Vue guarantees refs
// are bound, so it handles the initial load; the watcher only reacts to the
// key changing afterwards (e.g. arriving after an async settings fetch).
onMounted(() => {
  if (googleMapsApiKey.value) tryInitMap(googleMapsApiKey.value);
});

watch(
  () => googleMapsApiKey.value,
  (newKey) => {
    if (newKey) tryInitMap(newKey);
  },
);
onBeforeUnmount(() => {
  if (markerInstance) markerInstance.setMap(null);
  map = null;
});
</script>

<template>
  <div
    ref="mapEl"
    :class="['network__map', `network__map--${marker.template}`]"
    :aria-label="marker.title"
  ></div>
</template>

<style scoped lang="scss">
.network {
  $parent: &;
  &--white {
    #{$parent} {
    }
  }
  &--black {
    #{$parent} {
      &__map {
        background: $lime-green;
      }
    }
  }
  &--gray {
    #{$parent} {
      &__map {
        background: $lime-green;
      }
    }
  }
  &__map {
    border-radius: 0 0 8px 8px;
    height: 620px;
    @include media-breakpoint-up(sm) {
      height: 840px;
    }
    @include media-breakpoint-up(md) {
      height: 600px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 600);
      @include responsive-spacing(border-bottom-left-radius, 8);
      @include responsive-spacing(border-bottom-right-radius, 8);
    }
  }
}
</style>